import * as React from 'react'
import clsx from 'clsx'
import { useForkRef } from '@applift/factor'
import { GridRowEventLookup } from '../models/events'
import { useGridApiContext } from '../hooks/utils/useGridApiContext'
import { useGridRootProps } from '../hooks/utils/useGridRootProps'
import { DataGridProps } from '../models/props/DataGridProps'
import { gridColumnsTotalWidthSelector } from '../hooks/features/columns/gridColumnsSelector'
import { findParentElementFromClassName } from '../utils/domUtils'
import { GridCellProps } from './cell/GridCell'
import { ReactTableCell, ReactTableRow } from '../models/gridReactTable'
import { ReactVirtualItem } from '../models/gridReactVirtual'
import { GRID_CHECKBOX_SELECTION_FIELD } from '../colDef/gridCheckboxSelectionColDef'
import styles from '../style'
import { useGridSelector } from '../hooks'
import { gridEditRowsStateSelector } from '../hooks/features/editing/gridEditingSelectors'
import { GridCellModes } from '../models/gridEditRowModel'

export interface GridRowProps extends ReactTableRow {
  rowHeight: number | 'auto'
  containerWidth: number
  side: 'left' | 'center' | 'right'
  colRange: ReactVirtualItem[]
  onClick?: React.MouseEventHandler<HTMLDivElement>
  onDoubleClick?: React.MouseEventHandler<HTMLDivElement>
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>
  /**
   * Determines which cell has focus.
   * If `null`, no cell in this row has focus.
   */
  focusedCell: string | null
  /**
   * Determines which cell should be tabbable by having tabIndex=0.
   * If `null`, no cell in this row is in the tab sequence.
   */
  tabbableCell: string | null
}

function EmptyCell({
  width,
  classes,
}: {
  width: number
  classes: DataGridProps['classes']
}) {
  if (!width) {
    return null
  }
  return <div className={clsx(styles.cell, classes?.cell)} style={{ width }} />
}

export const GridRow = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & GridRowProps
>(function GridRow(props, refProp) {
  const {
    id: rowId,
    index,
    style: styleProp,
    rowHeight,
    containerWidth,
    onClick,
    onDoubleClick,
    onMouseEnter,
    onMouseLeave,
    getIsSelected,
    getLeftVisibleCells,
    getRightVisibleCells,
    getCenterVisibleCells,
    getVisibleCells,
    side,
    colRange,
    focusedCell,
    tabbableCell,
  } = props
  const apiRef = useGridApiContext()
  const ref = React.useRef<HTMLDivElement>(null)
  const rootProps = useGridRootProps()
  const editRowsState = useGridSelector(apiRef, gridEditRowsStateSelector)
  const columnsTotalWidth = gridColumnsTotalWidthSelector(apiRef.current)
  const headersMaxDepth = apiRef.current.tableInstance.getHeaderGroups().length
  const showColumnGrouping = rootProps.showColumnGrouping
  const handleRef = useForkRef(ref, refProp)
  const ariaRowIndex = index + (showColumnGrouping ? headersMaxDepth + 1 : 2)
  const { hasScrollX } = apiRef.current.getRootDimensions() ?? {
    hasScrollX: false,
    hasScrollY: false,
  }
  const selected = getIsSelected()
  const emptyCellWidth =
    side === 'center' ? containerWidth - columnsTotalWidth : 0
  const minHeight = rowHeight
  const style = {
    ...styleProp,
    maxHeight: rowHeight === 'auto' ? 'none' : rowHeight, // max-height doesn't support "auto"
    minHeight,
  }
  const cellsToRender =
    side === 'left'
      ? getLeftVisibleCells()
      : side === 'right'
        ? getRightVisibleCells()
        : getCenterVisibleCells()

  const firstColumnToRender =
    side === 'left'
      ? 0
      : side === 'right'
        ? getLeftVisibleCells().length + getCenterVisibleCells().length
        : getLeftVisibleCells().length

  const visibleColumns = getVisibleCells().length - 1

  const rightCellsLength = getRightVisibleCells().length

  const publish = React.useCallback(
    (
      eventName: keyof GridRowEventLookup,
      propHandler: React.MouseEventHandler<HTMLDivElement> | undefined
    ): React.MouseEventHandler<HTMLDivElement> =>
      event => {
        // Ignore portal
        // The target is not an element when triggered by a Select inside the cell
        // See https://github.com/mui/material-ui/issues/10534
        if (
          (event.target as any).nodeType === 1 &&
          !event.currentTarget.contains(event.target as Element)
        ) {
          return
        }

        // The row might have been deleted
        if (!apiRef.current.tableInstance.getRow(rowId)) {
          return
        }

        apiRef.current.publishEvent(eventName, { rowId }, event)

        if (propHandler) {
          propHandler(event)
        }
      },
    [apiRef, rowId]
  )

  const publishClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const cell = findParentElementFromClassName(
        event.target as HTMLDivElement,
        styles.cell
      )
      const field = cell?.getAttribute('data-field')
      // Check if the field is available because the cell that fills the empty
      // space of the row has no field.
      if (field) {
        // User clicked in the checkbox added by checkboxSelection
        if (field === GRID_CHECKBOX_SELECTION_FIELD) {
          return
        }
        if (
          apiRef.current.tableInstance.getColumn(field)?.columnDef.meta
            ?.type === 'actions'
        ) {
          return
        }
        if (apiRef.current.getCellMode(rowId, field) === GridCellModes.Edit) {
          return
        }
      }
      publish('rowClick', onClick)(event)
    },
    [onClick, publish, rowId, apiRef]
  )

  const getCell = React.useCallback(
    (
      column: ReactTableCell,
      cellProps: Pick<GridCellProps, 'showRightBorder' | 'colIndex'>
    ) => {
      const editCellState = editRowsState[rowId]
        ? editRowsState[rowId]![column.column.id]
        : null

      return (
        <rootProps.components.Cell
          {...rootProps.componentsProps?.cell}
          key={column.id}
          height={rowHeight}
          rowId={rowId}
          showRightBorder={cellProps.showRightBorder}
          colIndex={cellProps.colIndex}
          hasFocus={column.column.id === focusedCell}
          tabIndex={column.column.id === tabbableCell ? 0 : -1}
          editCellState={editCellState}
          {...column}
        />
      )
    },
    [rootProps, rowHeight, rowId, focusedCell, tabbableCell, editRowsState]
  )

  const cells: JSX.Element[] = []

  if (side === 'left' || side === 'right') {
    for (let i = 0; i < cellsToRender.length; i++) {
      cells.push(
        getCell(cellsToRender[i]!, {
          showRightBorder: rootProps.showCellRightBorder,
          colIndex: firstColumnToRender + i,
        })
      )
    }
  } else {
    for (let i = 0; i < colRange.length; i += 1) {
      const currentIndex = colRange[i]!.index!
      const currentCell = cellsToRender[currentIndex]!
      const indexRelativeToAllColumns = firstColumnToRender + currentIndex
      const isLastColumn =
        indexRelativeToAllColumns + rightCellsLength === visibleColumns
      const removeLastBorderRight = isLastColumn && !hasScrollX
      const showRightBorder = !isLastColumn
        ? rootProps.showCellRightBorder
        : !removeLastBorderRight && rootProps.disableExtendRowFullWidth
      const cellProps = {
        showRightBorder,
        colIndex: indexRelativeToAllColumns,
      }
      cells.push(getCell(currentCell, cellProps))
    }
  }

  const eventHandlers = {
    onClick: publishClick,
    onDoubleClick: publish('rowDoubleClick', onDoubleClick),
    onMouseEnter: publish('rowMouseEnter', onMouseEnter),
    onMouseLeave: publish('rowMouseLeave', onMouseLeave),
  }

  return (
    <div
      ref={handleRef}
      data-id={rowId}
      data-rowindex={index}
      role="row"
      className={clsx(
        styles.row,
        rootProps.classes?.row,
        selected && [
          styles['row--selected'],
          rootProps.classes?.['row--selected'],
        ],
        rowHeight === 'auto' && [
          styles['row--dynamicHeight'],
          rootProps.classes?.['row--dynamicHeight'],
        ]
      )}
      aria-rowindex={ariaRowIndex}
      aria-selected={selected}
      style={style}
      {...eventHandlers}
    >
      {cells}
      {emptyCellWidth > 0 && (
        <EmptyCell width={emptyCellWidth} classes={rootProps.classes} />
      )}
    </div>
  )
})
